/* eslint-disable react-hooks/exhaustive-deps */
import { gql, useLazyQuery, useMutation } from '@apollo/client';
import {
	AlertDialog,
	AlertDialogBody,
	AlertDialogContent,
	AlertDialogFooter,
	AlertDialogHeader,
	AlertDialogOverlay,
	Box,
	Button,
	Center,
	Container,
	Flex,
	Table,
	Tbody,
	Td,
	Text,
	Th,
	Thead,
	Tr,
	useDisclosure,
	useToast,
} from '@chakra-ui/react';
import { ButtonDropdown } from '@cloudscape-design/components';
import { faChevronLeft, faChevronRight, faPlus } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import ReactPaginate from 'react-paginate';
import { useHistory, useLocation } from 'react-router-dom';
import BreadcrumbComponent from '../../../../components/BreadcrumbComponent/BreadcrumbComponent';
import CompanyName from '../../../../components/CompanyName.js';
import LabelDate from '../../../../components/LabelDate.js';
import NoData from '../../../../components/NoData.js';
import CompanyBadge from '../../../../components/Other/CompanyBadge.js';
import PrintLayout from '../../../../components/Print/PrintLayout';
import LoadingTable from '../../../../components/Table/Loading.js';
import TotalRow from '../../../../components/TotalRow.js';
import c from '../../../../constant';
import useStore from '../../../../store/index.js';
import CreateRatioDocument from '../create-ratio-document.js';
import Filter from './risk-indicator-filter.js';

const Q_LIST = gql`
	query get($pagerInput: PagerInput!, $companyType: InsuranceInstitutionsType, $companyId: String, $year: Float, $level: EwsRiskRankingLevel) {
		getAnnuallyFinancialStatementsRatio(pagerInput: $pagerInput, companyType: $companyType, companyId: $companyId, year: $year, level: $level) {
			list {
				id
				company {
					id
					name
					nameKhmer
					type
					logo
				}
				year
				createdAt
				lifeReport {
					id
					companyRiskRanking
				}
				nonLifeReport {
					id
					companyRiskRanking
				}
			}
			totalRows
			totalPages
		}
	}
`;

const M_DELETE = gql`
	mutation RemoveRatioStatement($id: String!) {
		removeAnnuallyFinancialStatementsRatio(id: $id)
	}
`;

function AnnualFinancialStatementRatioList() {
	const deleteDialogRef = useRef();
	const deleteDialogCancelRef = useRef();
	const toast = useToast();
	const location = useLocation();
	const urlParams = new URLSearchParams(location.search);
	const page = urlParams.get('page') || 1;
	const companyId = urlParams.get('companyId') || null;
	const companyType = urlParams.get('companyType') || null;
	const year = urlParams.get('year') || null;
	const level = urlParams.get('level') || null;
	const { push } = useHistory();
	const { currentUser: user } = useStore((state) => state?.currentUser);
	const { t } = useTranslation();
	const { isOpen, onOpen, onClose } = useDisclosure();
	const [deleteId, setDeleteId] = useState(null);
	const [loadRadios, { loading, data, error }] = useLazyQuery(Q_LIST);
	const [deleteRadio, { loading: loadingDelete }] = useMutation(M_DELETE);

	const canCreate = useMemo(() => {
		const roles = user?.role?.split(',')?.map((r) => r.trim());
		if (roles?.includes('admin')) return true;
		if (roles?.includes('ews'))
			return (
				user?.ews?.includes('ewsCanViewStatementGeneralInsurance') ||
				user?.ews?.includes('ewsCanViewStatementLifeInsurance') ||
				user?.ews?.includes('ewsCanViewStatementMicroInsurance')
			);
		return false;
	}, [user]);

	useEffect(load, [page, companyType, companyId, year, level]);

	function load() {
		const limit = 10;
		const offset = parseInt(page ? (page - 1) * limit : 0) || 0;
		const variables = {
			pagerInput: { limit, offset },
			companyId,
			companyType,
			year: parseInt(year),
			level,
		};
		loadRadios({ variables });
	}

	function handlePageChanged({ selected }) {
		let page = selected + 1;
		let url = '?page=' + page;

		if (companyType) {
			url = url + '&companyType=' + companyType;
		}

		if (companyId) {
			url = url + '&companyId=' + companyId;
		}

		if (year) {
			url = url + '&year=' + year;
		}

		if (level) {
			url = url + '&level=' + level;
		}

		push(`/early-warning-system/annual-financial-statement-ratio/list${url}`);
	}

	function pushDetail({ id, year, companyType, companyId }) {
		const url = '?statementId=' + id + '&year=' + year + '&companyType=' + companyType + '&companyId=' + companyId;
		push('/early-warning-system/annual-financial-statement-ratio/detail' + url);
	}

	function ActionMenu({ row = null }) {
		return (
			<Box onClick={(e) => e.stopPropagation()}>
				<Flex justifyContent="flex-end" alignItems="center">
					<ButtonDropdown
						className="action-button-table"
						variant="inline-icon"
						ariaLabel={t('Actions')}
						expandToViewport={true}
						items={[{ id: 'delete', text: <p style={{ color: 'red' }}>{t('Delete')}</p> }]}
						onItemClick={({ detail }) => {
							if (detail?.id === 'delete') {
								setDeleteId(row?.id);
							}
						}}
					/>
				</Flex>
			</Box>
		);
	}

	function RiskRanking({ companyType = null, lifeRiskRanking = null, nonLifeRiskRanking = null }) {
		return (
			<>
				{['LIFE_INSURANCE'].includes(companyType) && <Text fontWeight="medium">{lifeRiskRanking}</Text>}
				{['GENERAL_INSURANCE', 'MICRO_INSURANCE', 'REINSURANCE'].includes(companyType) && <Text fontWeight="medium">{nonLifeRiskRanking}</Text>}
			</>
		);
	}

	return (
		<PrintLayout>
			<>
				<Container maxW="container.xl" mt="16px" mb="16px">
					<BreadcrumbComponent
						list={[
							{
								name: 'Early Warning System',
								path: '#',
							},
						]}
					/>
					<Center className="show-only-printing">
						<Text textAlign="center" color="#62B3ED" fontFamily="Moul" fontSize={18}>
							{t('Annual Key Risk Indicator')}
						</Text>
					</Center>
					<Center mt="5">
						<Box
							w="100%"
							bg="white"
							borderRadius={c.borderRadius}
							border="1px solid #dbdbdb"
							boxShadow="0 1px 2px rgba(var(--black, #000), 0.1)"
							pb={8}
						>
							<Flex
								className="hidden-print responsive-header-for-table"
								bg="#FAFAFA"
								p="16px"
								pt="8px"
								pb="8px"
								mb="16px"
								alignItems="center"
								justifyContent="space-between"
								borderBottom="1px solid #dbdbdb"
							>
								<Text fontSize="x-large" fontWeight="bold">
									{t('Annual Key Risk Indicator')}
								</Text>
								<Flex direction={{ base: 'column', md: 'row' }} justifyContent="flex-end" alignItems="center" className="hidden-print">
									{canCreate && (
										<Button
											className="hidden-print"
											isLoading={false}
											isDisabled={false}
											size="sm"
											leftIcon={<FontAwesomeIcon icon={faPlus} style={{ fontSize: 16 }} />}
											borderRadius={c.borderRadius}
											border="1px solid #bdc3c7"
											width={['100%', '100%', 'auto']}
											colorScheme="gray"
											variant="solid"
											onClick={onOpen}
											mr={2}
										>
											{t('Create')}
										</Button>
									)}
									<Filter
										companyType={companyType}
										companyId={companyId}
										year={year}
										level={level}
										onCallback={(url) => push(`/early-warning-system/annual-financial-statement-ratio/list${url}`)}
									/>
								</Flex>
							</Flex>
							<Box pl={4} pr={4}>
								<Table variant="simple" className="table-company-proposal-list responsive-table-container">
									<Thead>
										<Tr className="td-as-th">
											<Th align="left">
												<Text textAlign="left" color="gray.600">
													{t('Company')}
												</Text>
											</Th>
											<Th align="left">
												<Text textAlign="left" color="gray.600">
													{t('Type')}
												</Text>
											</Th>
											<Th align="left">
												<Text textAlign="left" color="gray.600">
													{t('Year')}
												</Text>
											</Th>
											<Th align="left">
												<Text textAlign="left" color="gray.600">
													{t('Risk Ranking')}
												</Text>
											</Th>
											<Th align="left">
												<Text textAlign="left" color="gray.600">
													{t('Submitted At')}
												</Text>
											</Th>
											<Th align="right" className="hidden-print">
												<Text textAlign="right" color="gray.600">
													{t('Actions')}
												</Text>
											</Th>
										</Tr>
									</Thead>
									<Tbody>
										{loading ? (
											<LoadingTable column={6} row={5} rowHeight="22px" />
										) : (
											<>
												{error ? (
													<Tr>
														<Td colSpan={6}>
															<Flex alignItems="center" justifyContent="center" pt={8} pb={8}>
																<Text fontSize="md" fontWeight="bold" color="red.400">
																	{t('something went wrong')}
																</Text>
															</Flex>
														</Td>
													</Tr>
												) : (
													<>
														{data?.getAnnuallyFinancialStatementsRatio?.list?.length > 0 ? (
															<>
																{data?.getAnnuallyFinancialStatementsRatio?.list?.map((e, i) => {
																	return (
																		<Tr
																			key={`report-item-${i}`}
																			onClick={() =>
																				pushDetail({
																					id: e?.id,
																					year: e?.year,
																					companyType: e?.company?.type,
																					companyId: e?.company?.id,
																				})
																			}
																		>
																			<Td align="left">
																				<CompanyName company={e?.company} />
																			</Td>
																			<Td align="left">
																				<CompanyBadge type={e?.company?.type} />
																			</Td>
																			<Td align="left">
																				<LabelDate input={parseInt(e?.year)} inputFormat="Y" outputFormat="YYYY" />
																			</Td>
																			<Td align="left">
																				<RiskRanking
																					companyType={e?.company?.type}
																					lifeRiskRanking={e?.lifeReport?.companyRiskRanking}
																					nonLifeRiskRanking={e?.nonLifeReport?.companyRiskRanking}
																				/>
																			</Td>
																			<Td align="left">
																				<LabelDate input={e?.createdAt} outputFormat={c.dateTimeFormat} />
																			</Td>
																			<Td align="right" className="hidden-print">
																				<ActionMenu row={e} />
																			</Td>
																		</Tr>
																	);
																})}
															</>
														) : (
															<Tr>
																<Td colSpan={6}>
																	<Flex alignItems="center" justifyContent="center" pt={8} pb={8}>
																		<NoData />
																	</Flex>
																</Td>
															</Tr>
														)}
													</>
												)}
											</>
										)}
									</Tbody>
									<tfoot className="responsive-footer hidden-print">
										<Tr className="td-as-th">
											<Th align="left">
												<Text textAlign="left" color="gray.600">
													{t('Company')}
												</Text>
											</Th>
											<Th align="left">
												<Text textAlign="left" color="gray.600">
													{t('Type')}
												</Text>
											</Th>
											<Th align="left">
												<Text textAlign="left" color="gray.600">
													{t('Year')}
												</Text>
											</Th>
											<Th align="left">
												<Text textAlign="left" color="gray.600">
													{t('Risk Ranking')}
												</Text>
											</Th>
											<Th align="left">
												<Text textAlign="left" color="gray.600">
													{t('Submitted At')}
												</Text>
											</Th>
											<Th align="right" className="hidden-print">
												<Text textAlign="right" color="gray.600">
													{t('Actions')}
												</Text>
											</Th>
										</Tr>
									</tfoot>
								</Table>
								{data?.getAnnuallyFinancialStatementsRatio?.totalRows > 0 && (
									<>
										<Flex justifyContent="flex-end" direction="row">
											<TotalRow count={data?.getAnnuallyFinancialStatementsRatio?.totalRows} mt={2} />
										</Flex>
									</>
								)}
								{data?.getAnnuallyFinancialStatementsRatio?.totalPages > 1 && (
									<>
										<Flex alignItems="flex-end" direction="column" mt={4}>
											<Flex alignItems="flex-end" direction="column">
												<ReactPaginate
													previousLabel={<FontAwesomeIcon icon={faChevronLeft} style={{ fontSize: 12, cursor: 'pointer' }} />}
													nextLabel={<FontAwesomeIcon icon={faChevronRight} style={{ fontSize: 12, cursor: 'pointer' }} />}
													breakLabel={'...'}
													breakClassName={'break-me'}
													disableInitialCallback={true}
													pageCount={data?.getAnnuallyFinancialStatementsRatio?.totalPages}
													marginPagesDisplayed={2}
													pageRangeDisplayed={3}
													onPageChange={handlePageChanged}
													containerClassName={'pagination'}
													activeClassName={'active'}
													initialPage={(page - 1) | 0}
												/>
											</Flex>
										</Flex>
									</>
								)}
							</Box>
						</Box>
					</Center>
				</Container>
				{deleteId && (
					<AlertDialog
						isOpen={true}
						closeOnEsc={loadingDelete ? false : true}
						closeOnOverlayClick={loadingDelete ? false : true}
						leastDestructiveRef={deleteDialogRef}
						onClose={() => setDeleteId(null)}
					>
						<AlertDialogOverlay>
							<AlertDialogContent>
								<AlertDialogHeader fontSize="lg" fontWeight="bold">
									{t('Confirmation')}
								</AlertDialogHeader>
								<AlertDialogBody>
									<Text fontSize="15px">{t("Are you sure? You can't undo this action afterwards")}</Text>
								</AlertDialogBody>
								<AlertDialogFooter>
									<Button mr={4} isLoading={loadingDelete} ref={deleteDialogCancelRef} onClick={() => setDeleteId(null)}>
										{t('Cancel')}
									</Button>
									<Button
										isLoading={loadingDelete}
										colorScheme="red"
										onClick={() => {
											deleteRadio({ variables: { id: deleteId } })
												.then((result) => {
													setDeleteId(null);
													load();
													toast({
														title: t('Operation success'),
														status: 'success',
														isClosable: true,
														position: 'top-right',
													});
												})
												.catch((error) => {
													toast({
														title: t(error?.message || 'something went wrong'),
														status: 'error',
														isClosable: true,
														position: 'bottom-right',
													});
												});
										}}
									>
										{t('Delete')}
									</Button>
								</AlertDialogFooter>
							</AlertDialogContent>
						</AlertDialogOverlay>
					</AlertDialog>
				)}
				{isOpen && <CreateRatioDocument isOpen={isOpen} onClose={onClose} onCallback={pushDetail} />}
			</>
		</PrintLayout>
	);
}

export default AnnualFinancialStatementRatioList;
